// The caller's approximate-sharing bubbles, with start and end. The live map
// learns about a change from the stream; this list drives the controls on
// /account and the circle page.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { bubbleErrorText, bubbleRequest, type BubbleDuration } from "./bubbles";

export const BUBBLES_KEY = ["me", "sharing-bubbles"] as const;

export interface BubbleStart {
  circleId: string | null;
  radiusM: number;
  hours: BubbleDuration;
}

/** `pollMs`: refetch this often (a guardian may start or end the caller's bubble for their circle). */
export function useSharingBubbles(pollMs: number | false = false) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: BUBBLES_KEY,
    refetchInterval: pollMs,
    queryFn: async () => unwrap(await api.GET("/me/sharing-bubbles")),
  });
  const settle = () => queryClient.invalidateQueries({ queryKey: BUBBLES_KEY });
  const start = useMutation({
    mutationFn: async ({ circleId, radiusM, hours }: BubbleStart) =>
      unwrap(await api.POST("/me/sharing-bubbles", { body: bubbleRequest(circleId, radiusM, hours) })),
    onSettled: settle,
    // A supervision started or tightened since the page loaded: the forms should ask instead.
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "supervised") {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["circles"] }),
          queryClient.invalidateQueries({ queryKey: ["circle"] }),
        ]);
      }
    },
  });
  const end = useMutation({
    mutationFn: async (bubble: Schemas["SharingBubble"]) => {
      const result = await api.DELETE("/me/sharing-bubbles/{bubbleId}", {
        params: { path: { bubbleId: bubble.id } },
      });
      // Already over (expired, ended by an SOS or elsewhere) is what we wanted.
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSettled: settle,
  });
  const error = start.error ?? end.error;
  return {
    bubbles: list.data ?? [],
    loading: list.isPending,
    listError: list.error ? errorMessage(list.error) : null,
    start,
    end,
    busy: start.isPending || end.isPending,
    error: error ? bubbleErrorText(error instanceof ApiError ? error.code : undefined, errorMessage(error)) : null,
  };
}
